import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Gauge, ChevronDown, ChevronUp, SlidersHorizontal, RotateCcw, ShieldCheck, Loader2, AlertTriangle, Eye, Info } from "lucide-react";
import { ScoringService } from "@/components/ScoringService";

const CONF_CLS = {
  "High confidence": "bg-teal-600 text-white border-teal-600",
  "Moderate confidence": "bg-teal-50 text-teal-700 border-teal-300",
  "Low confidence": "bg-amber-50 text-amber-700 border-amber-300",
  "Insufficient evidence": "bg-slate-100 text-slate-500 border-slate-300",
};
const SAL_CLS = {
  "High salience": "bg-indigo-600 text-white border-indigo-600",
  "Moderate salience": "bg-indigo-50 text-indigo-700 border-indigo-300",
  "Low salience": "bg-slate-100 text-slate-500 border-slate-300",
};
const QCOV_CLS = {
  "Adequately answered": "bg-teal-50 text-teal-700 border-teal-200",
  "Partially answered": "bg-amber-50 text-amber-700 border-amber-200",
  "Insufficient evidence": "bg-red-50 text-red-700 border-red-200",
  "Not assessed": "bg-slate-100 text-slate-400 border-slate-200",
};

const barColor = (s, missing, aiOnly) => (missing ? "#94A3B8" : aiOnly ? "#8B5CF6" : s >= 75 ? "#0D9488" : s >= 50 ? "#0D9488" : s >= 30 ? "#D97706" : "#DC2626");

function DimRow({ d, s, weight, denom, missing, aiOnly, itemId }) {
  const [rubric, setRubric] = useState(false);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-x-4 gap-y-1" data-testid={`dim-row-${itemId}-${d.id}`}>
      <div className="flex items-center justify-between lg:justify-start gap-2">
        <button onClick={() => setRubric(!rubric)} className="text-xs font-semibold text-slate-700 hover:text-teal-700 text-left" data-testid={`rubric-toggle-${itemId}-${d.id}`}>{d.name}</button>
        <span className="font-mono text-[9px] text-slate-400">w {weight}%</span>
      </div>
      <div>
        {missing ? (
          <p className="text-[11px] text-slate-400 italic">{missing.state} — {missing.note}</p>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${s.score}%`, backgroundColor: barColor(s.score, false, aiOnly) }} />
              </div>
              <span className="font-mono text-xs text-slate-600 w-8 text-right">{s.score}</span>
              <span className="font-mono text-[9px] text-slate-400 w-14 text-right">+{((s.score * weight) / denom).toFixed(1)} pts</span>
            </div>
            <p className="text-[11px] text-slate-500 leading-snug mt-0.5">{s.rationale}</p>
          </>
        )}
        {rubric && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 mt-1.5" data-testid={`rubric-${itemId}-${d.id}`}>
            <p className="text-[10px] text-slate-500 mb-1.5">{d.description}</p>
            {d.rubric.map((r) => (
              <p key={r.value} className="text-[10px] text-slate-500"><span className="font-mono font-semibold text-slate-600 mr-1.5">{r.value}</span>{r.anchor}</p>
            ))}
            <p className="text-[9.5px] text-slate-400 mt-1">Intermediate values require a written rationale.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function ScoreCard({ item, dims, salDims, qcov }) {
  const [open, setOpen] = useState(false);
  const aiOnly = item.review_status.startsWith("AI-proposed");
  const missingIds = Object.fromEntries((item.missing || []).map((m) => [m.component, m]));
  const denom = dims.filter((d) => !missingIds[d.id]).reduce((a, d) => a + d.weight, 0) || 100;
  const p = item.provenance || {};
  return (
    <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm" data-testid={`calibration-item-${item.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`calibration-toggle-${item.id}`}>
        <div className="min-w-0 flex-1">
          <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400">{item.entity_type} · {item.framework.name} {item.framework.version}</p>
          <h4 className="text-sm font-bold text-slate-900 leading-snug mt-0.5">{item.name}</h4>
          <p className="text-[10px] text-slate-400 mt-1">
            {item.reviewer || "Reviewer assignment pending"} · {item.review_status} · {item.last_calibrated}
            <span className="text-amber-600/80 font-mono uppercase ml-2">Illustrative calibration — not a validated measurement scale</span>
          </p>
          <div className="flex flex-wrap gap-1 mt-1.5">
            {item.caps.length > 0 && <span className="text-[9px] bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-1.5 py-0.5" data-testid={`cap-indicator-${item.id}`}>{item.applied_cap ? "Confidence capped" : "Confidence cap applies"}</span>}
            {item.provisional && <span className="text-[9px] bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-1.5 py-0.5">Missing required component</span>}
            {p.adjudication && <span className="text-[9px] bg-violet-50 text-violet-700 border border-violet-200 rounded-full px-1.5 py-0.5">Reviewer disagreement — adjudicated</span>}
            {aiOnly && <span className="text-[9px] bg-violet-50 text-violet-700 border border-violet-200 rounded-full px-1.5 py-0.5">AI-proposed — awaiting review</span>}
            {item.sensitivity.unstable && <span className="text-[9px] bg-slate-100 text-slate-500 border border-slate-200 rounded-full px-1.5 py-0.5">Threshold-adjacent</span>}
          </div>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <div className="text-right">
            <p className="font-mono text-[8.5px] uppercase tracking-wide text-slate-400">Scientific confidence</p>
            <Badge variant="outline" className={`text-[10px] font-semibold ${CONF_CLS[item.label]}`} data-testid={`confidence-label-${item.id}`}>{item.label.replace(" confidence", "")}</Badge>
          </div>
          <div className="text-right">
            <p className="font-mono text-[8.5px] uppercase tracking-wide text-slate-400">Strategic salience</p>
            <Badge variant="outline" className={`text-[10px] font-semibold ${SAL_CLS[item.salience_label]}`} data-testid={`salience-label-${item.id}`}>{item.salience_label.replace(" salience", "")}</Badge>
          </div>
          {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-slate-100 pt-4 space-y-4" data-testid={`calibration-breakdown-${item.id}`}>
          {item.applied_cap && (
            <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5" data-testid={`cap-detail-${item.id}`}>
              Calculated score: {item.confidence_score} ({item.computed_label}). Final confidence: <span className="font-semibold">{item.label}</span>. Confidence capped — {item.applied_cap.rule}: {item.applied_cap.rationale}
            </p>
          )}
          {item.caps.length > 0 && !item.applied_cap && (
            <p className="text-[10.5px] text-slate-500">Applicable cap: {item.caps.map((c) => `${c.rule} (max ${c.max})`).join("; ")} — does not alter the current category.</p>
          )}
          <div>
            <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-2">
              Scientific confidence — calibrated decision-support index {item.confidence_score}/100 · Σ(score × weight)/Σ(applicable weights) · framework {item.framework.version}
            </p>
            <div className="space-y-3">
              {dims.map((d) => <DimRow key={d.id} d={d} s={item.scores[d.id]} weight={d.weight} denom={denom} missing={missingIds[d.id]} aiOnly={aiOnly} itemId={item.id} />)}
            </div>
            {p.denominator_note && <p className="text-[10px] text-slate-400 mt-2">{p.denominator_note}</p>}
          </div>
          <div>
            <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-2">Strategic salience — index {item.salience_score}/100 (never contributes to scientific confidence)</p>
            <div className="space-y-3">
              {salDims.map((d) => <DimRow key={d.id} d={d} s={item.scores[d.id]} weight={d.weight} denom={100} itemId={item.id} />)}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-2.5 text-[11px] text-slate-600 border-t border-slate-100 pt-3">
            <div data-testid={`reviewer-calibration-${item.id}`}>
              <p className="font-mono text-[9px] uppercase tracking-wide text-slate-400">Reviewer calibration (illustrative demonstration data)</p>
              <p>AI-proposed: {p.ai_score ?? "—"} · Reviewer 1: {p.r1_score ?? "Pending"} {p.r2_score != null && `· Reviewer 2: ${p.r2_score}`}</p>
              <p className="text-slate-500">{p.agreement}</p>
              {p.adjudication && <p className="text-violet-700 mt-0.5">Adjudicated: {p.adjudication.score} by {p.adjudication.by} ({p.adjudication.date}) — {p.adjudication.rationale}</p>}
              <p className="text-[10px] text-slate-400 mt-0.5">Adjudication required when reviewers differ by &gt;15 points or one confidence category.</p>
            </div>
            <div data-testid={`sensitivity-${item.id}`}>
              <p className="font-mono text-[9px] uppercase tracking-wide text-slate-400">Sensitivity analysis</p>
              <p>Most influential: {item.sensitivity.most_influential.map(([n, v]) => `${n} (+${v})`).join(", ")}</p>
              <p>Distance to nearest threshold: {item.sensitivity.distance_to_threshold} points</p>
              <p className={item.sensitivity.unstable ? "text-amber-700 font-semibold" : "text-slate-500"}>{item.sensitivity.note}</p>
            </div>
            <div>
              <p className="font-mono text-[9px] uppercase tracking-wide text-slate-400">Score provenance</p>
              <p>Framework: {item.framework.name} {item.framework.version} · Calibrated {item.last_calibrated} · Evidence cut-off {p.evidence_cutoff}</p>
              <p>Source set: {(p.source_set || []).join(", ") || "—"}</p>
              {p.previous && <p className="text-slate-500 mt-0.5">Previous: {p.previous.score} ({p.previous.label}) — {p.previous.reason}</p>}
            </div>
            <div>
              <p className="font-mono text-[9px] uppercase tracking-wide text-slate-400">Framework emphasis</p>
              <p>{item.framework.emphasis.join(" · ")}</p>
            </div>
          </div>
          {item.id === "cal-assessment" && qcov && (
            <div className="border-t border-slate-100 pt-3" data-testid="question-coverage">
              <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-2">Intelligence-question coverage (15 commissioned questions) — overall assessment cannot receive High confidence while important questions remain unsupported</p>
              <div className="flex flex-wrap gap-1.5">
                {qcov.map((qc) => (
                  <span key={qc.q} className={`text-[10px] border rounded-full px-2 py-0.5 ${QCOV_CLS[qc.status]}`}>{qc.q} · {qc.status}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Calibration() {
  const [data, setData] = useState(null);
  const [weights, setWeights] = useState({});
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [typeF, setTypeF] = useState("all");
  const [confF, setConfF] = useState("all");
  const [salF, setSalF] = useState("all");
  const [capF, setCapF] = useState(false);

  const load = (d) => {
    setData(d);
    setWeights(Object.fromEntries(d.dimensions.map((x) => [x.id, x.weight])));
    setPreview(null);
  };
  useEffect(() => { api.get("/calibration").then((r) => load(r.data)); }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.items.filter((i) =>
      (typeF === "all" || i.entity_type === typeF) &&
      (confF === "all" || i.label === confF) &&
      (salF === "all" || i.salience_label === salF) &&
      (!capF || i.caps.length > 0));
  }, [data, typeF, confF, salF, capF]);

  if (!data) return <div className="p-8 text-slate-400 text-sm">Loading calibration framework…</div>;

  const total = Object.values(weights).reduce((a, b) => a + (parseInt(b, 10) || 0), 0);
  const dirty = data.dimensions.some((d) => parseInt(weights[d.id], 10) !== d.weight);
  const wpayload = Object.fromEntries(Object.entries(weights).map(([k, v]) => [k, parseInt(v, 10) || 0]));

  const doPreview = () => {
    setBusy(true);
    api.post("/calibration/weights/preview", { weights: wpayload })
      .then((r) => setPreview(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Preview failed"))
      .finally(() => setBusy(false));
  };
  const save = () => {
    setBusy(true);
    api.put("/calibration/weights", { weights: wpayload, reason })
      .then((r) => { load(r.data); setReason(""); toast.success(`Framework ${r.data.framework_version} activated — scores recalculated as a new version`); })
      .catch((e) => toast.error(e.response?.data?.detail || "Update failed"))
      .finally(() => setBusy(false));
  };
  const reset = () => {
    setBusy(true);
    api.delete("/calibration/weights").then((r) => { load(r.data); toast.success("Rolled back to default framework v2.0"); }).finally(() => setBusy(false));
  };

  return (
    <div className="space-y-5" data-testid="calibration-view">
      <div className="bg-slate-900 rounded-xl p-5 border border-slate-800" data-testid="calibration-banner">
        <div className="flex gap-4 items-start">
          <div className="w-10 h-10 rounded-lg bg-teal-500/20 border border-teal-500/40 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-5 h-5 text-teal-400" />
          </div>
          <div>
            <h3 className="font-heading text-base font-bold text-white">Two frameworks, no unexplained scores</h3>
            <p className="text-sm text-slate-400 mt-1 leading-relaxed max-w-3xl">
              <span className="text-teal-300 font-semibold">Scientific Confidence</span> answers "how confidently is this finding supported and interpreted?" —
              <span className="text-indigo-300 font-semibold"> Strategic Salience</span> answers "how important is it for decision-making?".
              Novelty and strategic importance never increase scientific confidence, so a finding can correctly read
              "High strategic salience, low scientific confidence — validation required." Non-compensatory caps prevent strong components from concealing a critical weakness.
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              {data.labels.map((l) => (
                <span key={l.label} className="text-[10px] text-slate-300 bg-slate-800 border border-slate-700 rounded-full px-2.5 py-1" title={`${l.description} Interpretation: ${l.interpretation} Reporting: ${l.reporting_rule}`}>
                  <span className="font-semibold">{l.label}</span> · {l.range}
                </span>
              ))}
            </div>
            <p className="text-[10px] text-slate-500 mt-2 flex items-start gap-1"><Info className="w-3 h-3 shrink-0 mt-0.5 text-amber-400" />
              Thresholds are configurable prototype rules and are not externally validated clinical measurement scales. The Role Lens may reorder or emphasise items but never changes component scores, confidence, caps or reviewer decisions. Evidence cut-off: {data.evidence_cutoff}.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 mt-4 border-t border-slate-800 pt-3">
          {data.labels.map((l) => (
            <p key={l.label} className="text-[10.5px] text-slate-400"><span className="font-semibold text-slate-200">{l.label}:</span> {l.description} <span className="text-slate-500">Reporting: {l.reporting_rule}</span></p>
          ))}
        </div>
      </div>

      <ScoringService isReviewer={true} />

      <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm" data-testid="weight-config-panel">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h3 className="font-heading text-base font-bold text-slate-900 flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4 text-teal-600" />Scientific-confidence weights
            <span className="font-mono text-[10px] text-slate-400 font-normal">framework {data.framework_version} · administrator-controlled · changes create a new version</span>
          </h3>
          <div className="flex items-center gap-2">
            <span className={`font-mono text-xs ${total === 100 ? "text-teal-600" : "text-red-600"}`} data-testid="weight-total">Total: {total}/100</span>
            {data.weights_customized && (
              <Button variant="outline" className="h-7 text-[11px]" onClick={reset} disabled={busy} data-testid="reset-weights-btn"><RotateCcw className="w-3 h-3 mr-1" />Roll back to v2.0</Button>
            )}
            <Button variant="outline" className="h-7 text-[11px]" onClick={doPreview} disabled={busy || total !== 100 || !dirty} data-testid="preview-weights-btn"><Eye className="w-3 h-3 mr-1" />Preview impact</Button>
            <Button className="h-7 text-[11px] bg-teal-600 hover:bg-teal-700 text-white" onClick={save}
              disabled={busy || total !== 100 || !dirty || !reason.trim()} data-testid="save-weights-btn">
              {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}Activate new version
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
          {data.dimensions.map((d) => (
            <div key={d.id} className="flex items-start gap-3" data-testid={`weight-row-${d.id}`}>
              <Input type="number" min={0} max={100} value={weights[d.id]}
                onChange={(e) => setWeights((w) => ({ ...w, [d.id]: e.target.value }))}
                className="h-8 w-16 text-xs font-mono shrink-0" data-testid={`weight-input-${d.id}`} />
              <div className="min-w-0">
                <p className="text-xs font-semibold text-slate-800">{d.name}</p>
                <p className="text-[11px] text-slate-500 leading-snug">{d.description}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <Input placeholder="Change rationale * (required — recorded in the framework version audit)" value={reason} onChange={(e) => setReason(e.target.value)} className="h-8 text-xs flex-1 min-w-[260px]" data-testid="weight-reason-input" />
        </div>
        {preview && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mt-3" data-testid="weight-preview">
            <p className="text-[11px] font-semibold text-slate-700">Preview impact: {preview.affected} of {preview.total_items} scores change · {preview.category_changes.length} change confidence category</p>
            {preview.score_changes.map((c) => (
              <p key={c.id} className="text-[10.5px] text-slate-500 mt-0.5">{c.name}: {c.from_score} → {c.to_score} {c.from_label !== c.to_label && <span className="text-amber-700 font-semibold">({c.from_label} → {c.to_label})</span>} {c.capped && "· cap applies"}</p>
            ))}
            <p className="text-[10px] text-slate-400 mt-1">Historical scores are not overwritten — activation creates a new framework version.</p>
          </div>
        )}
        {(data.weight_history || []).length > 0 && (
          <div className="mt-3 border-t border-slate-100 pt-2" data-testid="framework-history">
            <p className="font-mono text-[9px] uppercase tracking-wide text-slate-400 mb-1">Framework versions</p>
            {data.weight_history.slice().reverse().map((h) => (
              <p key={h.version} className="text-[10.5px] text-slate-500">{h.version} · {h.date} · {h.by} — {h.reason}</p>
            ))}
          </div>
        )}
        <p className="text-[10px] text-slate-400 mt-2">Salience weights (Strategic importance {data.salience_dimensions[0].weight} / Novelty {data.salience_dimensions[1].weight}) are fixed in this prototype and never feed scientific confidence. Missing components are excluded from the denominator, not scored as zero.</p>
      </div>

      <div>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <h3 className="font-heading text-base font-bold text-slate-900 flex items-center gap-2">
            <Gauge className="w-4 h-4 text-teal-600" />Calibrated units
            <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400 font-normal">{filtered.length} of {data.items.length}</span>
          </h3>
          <div className="flex flex-wrap items-center gap-1.5 ml-auto">
            <Select value={typeF} onValueChange={setTypeF}>
              <SelectTrigger className="h-7 w-44 text-[11px]" data-testid="filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All object types</SelectItem>
                {[...new Set(data.items.map((i) => i.entity_type))].map((t) => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={confF} onValueChange={setConfF}>
              <SelectTrigger className="h-7 w-40 text-[11px]" data-testid="filter-confidence"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All confidence</SelectItem>
                {data.labels.map((l) => <SelectItem key={l.label} value={l.label} className="text-xs">{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={salF} onValueChange={setSalF}>
              <SelectTrigger className="h-7 w-40 text-[11px]" data-testid="filter-salience"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All salience</SelectItem>
                {data.salience_labels.map((l) => <SelectItem key={l.label} value={l.label} className="text-xs">{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
              <input type="checkbox" checked={capF} onChange={(e) => setCapF(e.target.checked)} className="accent-teal-600" data-testid="filter-capped" />With confidence cap
            </label>
          </div>
        </div>
        <div className="space-y-3">
          {filtered.map((it) => <ScoreCard key={it.id} item={it} dims={data.dimensions} salDims={data.salience_dimensions} qcov={data.question_coverage} />)}
          {filtered.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No scored units match the current filters.</p>}
        </div>
      </div>
    </div>
  );
}
